// Quick View Modal for Product Cards
if (!customElements.get('quick-view-modal')) {
  customElements.define(
    'quick-view-modal',
    class QuickViewModal extends HTMLElement {
      constructor() {
        super();
        this.modal = this.querySelector('.modal');
        this.dialog = this.querySelector('dialog');
        this.overlay = this.querySelector('.overlay');
        this.closeBtn = this.querySelector('.quick-view__close');
        this.loading = this.querySelector('.quick-view__loading');
        this.body = this.querySelector('.quick-view__body');
        this.galleryContainer = this.querySelector('.quick-view__gallery');
        this.infoContainer = this.querySelector('.quick-view__info');

        // Close handlers
        this.closeBtn?.addEventListener('click', () => this.hide());
        this.overlay?.addEventListener('click', () => this.hide());
        this.addEventListener('keyup', (evt) => {
          if (evt.code === 'Escape') this.hide();
        });
      }

      show() {
        this.dialog.setAttribute('open', '');
        this.modal.classList.add('modal--active');
        document.body.classList.add('modal--open');
        // Trap focus
        if (typeof trapFocus === 'function') {
          trapFocus(this);
        }
      }

      hide() {
        this.dialog.removeAttribute('open');
        this.modal.classList.remove('modal--active');
        document.body.classList.remove('modal--open');
        // Remove trap focus
        if (typeof removeTrapFocus === 'function') {
          removeTrapFocus(this);
        }
      }

      async loadProduct(handle) {
        // Prevent multiple simultaneous loads of the same product
        if (this.isLoading && this.currentHandle === handle) {
          return;
        }
        
        this.isLoading = true;
        this.currentHandle = handle;
        
        // Show loading state
        if (this.loading) this.loading.style.display = 'block';
        this.body.style.display = 'none';
        this.show();

        try {
          const response = await fetch(`/products/${handle}?view=quick-view`);
          if (!response.ok) {
            throw new Error('Failed to load product');
          }

          const html = await response.text();
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');

          // Extract the product section content (find by ID pattern)
          const productSection =
            doc.querySelector('[id^="QuickViewProduct-"]') || doc.querySelector('.product-wrapper');
          if (!productSection) {
            throw new Error('Product section not found');
          }

          // Clear and populate modal
          this.galleryContainer.innerHTML = '';
          this.infoContainer.innerHTML = '';

          // Extract gallery and info
          const gallery = productSection.querySelector('.product__media');
          const info = productSection.querySelector('.product__info-container');

          if (gallery) {
            this.galleryContainer.innerHTML = gallery.outerHTML;
          }
          if (info) {
            this.infoContainer.innerHTML = info.outerHTML;
          }

          // Re-initialize scripts
          this.initializeQuickViewScripts();

          // Hide loading, show content
          if (this.loading) this.loading.style.display = 'none';
          this.body.style.display = 'block';
          this.isLoading = false;
        } catch (error) {
          console.error('Error loading product:', error);
          if (this.loading) {
            this.loading.innerHTML = '<p>Error loading product. Please try again.</p>';
          }
          this.isLoading = false;
        }
      }

      initializeQuickViewScripts() {
        // Initialize Splide for gallery
        setTimeout(() => {
          const splideElement = this.galleryContainer.querySelector('[data-splide]');
          if (splideElement && typeof Splide !== 'undefined') {
            try {
              const splideOptions = JSON.parse(splideElement.getAttribute('data-splide') || '{}');
              // Ensure 2 images per scroll on desktop for quick view
              if (!splideOptions.breakpoints) {
                splideOptions.breakpoints = {};
              }
              if (!splideOptions.breakpoints['769']) {
                splideOptions.breakpoints['769'] = {};
              }
              splideOptions.breakpoints['769'].perPage = 2;

              const splide = new Splide(splideElement, splideOptions);
              splide.mount();
            } catch (e) {
              console.warn('Failed to initialize Splide:', e);
            }
          }
        }, 150);

        // Re-initialize variant selects (they should auto-initialize via custom element)
        // But ensure product-form.js is loaded
        const productForm = this.infoContainer.querySelector('product-form');
        if (productForm && !productForm.dataset.initialized) {
          productForm.dataset.initialized = 'true';
        }

        // Ensure variant input gets updated when variants change in quick view
        this.setupVariantInputUpdate();        
      }

      setupVariantInputUpdate() {
        // Wait for variant-selects to be initialized
        setTimeout(() => {
          const variantSelects = this.infoContainer.querySelector('variant-selects');
          const form = this.infoContainer.querySelector('form.product-form');

          if (!variantSelects || !form) return;

          // Get the hidden variant input
          const variantInput = form.querySelector('input[name="id"]');
          if (!variantInput) return;

          // Listen for variant changes
          const handleVariantChange = () => {
            // Get the current variant from variant-selects
            if (variantSelects.currentVariant && variantSelects.currentVariant.id) {
              variantInput.value = variantSelects.currentVariant.id;
              // Enable the input if it was disabled
              variantInput.removeAttribute('disabled');
              variantInput.dispatchEvent(new Event('change', { bubbles: true }));
            }
          };

          // Listen to all select changes within variant-selects
          const allSelects = variantSelects.querySelectorAll('select');
          allSelects.forEach((select) => {
            select.addEventListener('change', () => {
              // Small delay to allow VariantSelects class to update currentVariant
              setTimeout(handleVariantChange, 100);
              // Also update button state for "Select a Size"
              setTimeout(() => this.updateSelectSizeButton(), 150);
            });
          });

          // Also listen to the variant-selects change event if it exists
          variantSelects.addEventListener('change', () => {
            setTimeout(handleVariantChange, 100);
            setTimeout(() => this.updateSelectSizeButton(), 150);
          });
        }, 200);
      }      

      updateSelectSizeButton() {
        const variantSelects = this.infoContainer.querySelector('variant-selects');
        const form = this.infoContainer.querySelector('form.product-form');

        if (!variantSelects || !form) return;

        const addButton = form.querySelector('[name="add"]');
        const addButtonText = form.querySelector('[name="add"] > span');
        if (!addButton || !addButtonText) return;

        // Check if "Select a Size" is selected
        const sizeSelect = variantSelects.querySelector(
          'select[name*="Size"], select[name*="size"], select[name*="[Size"], select[name*="[size"]'
        );
        const isSelectSizeSelected = sizeSelect && sizeSelect.value === 'Select a Size';

        if (isSelectSizeSelected) {
          // Set button to "SELECT A SIZE" and disable it
          addButton.setAttribute('disabled', 'disabled');
          addButtonText.textContent = 'SELECT A SIZE';
        } else if (variantSelects.currentVariant) {
          // If a valid variant is selected, enable button
          if (variantSelects.currentVariant.available) {
            addButton.removeAttribute('disabled');
            if (window.variantStrings && window.variantStrings.addToCart) {
              addButtonText.textContent = window.variantStrings.addToCart;
            } else {
              addButtonText.textContent = 'Add to cart';
            }
          } else {
            // Variant is unavailable
            addButton.setAttribute('disabled', 'disabled');
            if (window.variantStrings && window.variantStrings.soldOut) {
              addButtonText.textContent = window.variantStrings.soldOut;
            } else {
              addButtonText.textContent = 'Sold out';
            }
          }
        } else {
          // No variant selected (shouldn't happen, but handle it)
          addButton.setAttribute('disabled', 'disabled');
          if (window.variantStrings && window.variantStrings.unavailable) {
            addButtonText.textContent = window.variantStrings.unavailable;
          } else {
            addButtonText.textContent = 'Unavailable';
          }
        }
      }      
    }
  );
}

// Handle quick view button clicks
// Use a flag to prevent multiple event listeners from being attached
if (!window.quickViewHandlerAttached) {
  window.quickViewHandlerAttached = true;
  
  document.addEventListener('click', (e) => {
    // Prevent programmatic clicks from predictive search
    if (e.detail === 0) {
      const predictiveSearch = e.target.closest('predictive-search');
      if (predictiveSearch) return;
    }

    const quickViewBtn = e.target.closest('.product-card__quick-view-btn');
    if (!quickViewBtn) return;

    e.preventDefault();
    e.stopPropagation(); // Prevent event bubbling
    
    const handle = quickViewBtn.dataset.quickViewHandle;
    if (!handle) return;

    // Get or create modal
    let modal = document.querySelector('quick-view-modal');
    if (!modal) {
      // Create modal if it doesn't exist - use the snippet template
      modal = document.createElement('quick-view-modal');
      // The modal HTML should be included via the snippet in the template
      // For now, create basic structure
      modal.innerHTML = `
        <div class="modal flex aic jcc">
          <div class="overlay modal__close quick-view-overlay"></div>
          <dialog class="modal__container quick-view__container">
            <button class="modal__close btn btn--unstyled content--floating quick-view__close" aria-label="Close">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 1L13 13M13 1L1 13" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              </svg>
            </button>
            <div class="quick-view__content">
              <div class="quick-view__loading">
                <div class="loading-overlay__spinner">
                  <svg aria-hidden="true" focusable="false" role="presentation" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
                    <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
                  </svg>
                </div>
              </div>
              <div class="quick-view__body" style="display: none;">
                <div class="quick-view__gallery"></div>
                <div class="quick-view__info"></div>
              </div>
            </div>
          </dialog>
        </div>
      `;
      document.body.appendChild(modal);
    }

    modal.loadProduct(handle);
  });
}
